import * as React from "react"
import { useTranslation } from "react-i18next"
import LocalizedLink from "./LocalizedLink";
import { useEffect } from "react";
import { useUser } from "../hooks/useUser";
import { CHUNHUIZK_CORPID } from "../libs/constants";
import { format } from "date-fns";
import ImageSlides from "./ImageSlides";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "./ui/dialog"
import ProductFeedbackForm from "./products/ProductFeedbackForm";
import { Button } from "./ui/button";
import { ShieldAlertIcon } from "lucide-react";

interface productFile {
    DownloadUrl: string
    file_id: string
    file_name: string
    file_size: 279175
    file_type: 1
    large_thumbnail_full_path: string;
    origin_link_url: null
    original_file_full_path: string
    original_file_name: string
    preview_url: string
    thumbnail_full_path: string
}

const ProductView = ({ product, locale = "zh-CN", isPreview = false }) => {
    const { t } = useTranslation()
    const { basicUserInfo } = useUser()

    const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState(false)

    const [internalViewPermission, setInternalViewPermission] = React.useState(false)

    const needTranslate = isPreview ? false : locale.slice(0, 2) !== 'zh'
    const languageCode = locale.split("-")[0]
    const i18nProduct = isPreview ? {} : product.i18n.find((p) => p.language === languageCode)

    const productName = (needTranslate && i18nProduct) ? i18nProduct.name : product.name
    const productInfo = (needTranslate && i18nProduct) ? i18nProduct.info : product.info
    const productCategoryName = (needTranslate && i18nProduct) ? i18nProduct.categoryName : product.categoryName
    const productTags = (needTranslate && i18nProduct) ? i18nProduct.tags : product.tags

    const tagIds = isPreview ? product.tags : product.tagIds
    const pictures = product.picture ? (JSON.parse(product.picture) as productFile[]) : []
    const attachments = product.attachment ? (JSON.parse(product.attachment) as productFile[]) : []

    const productStatus = product.productStatus

    useEffect(() => {

        if (productStatus === 'internal') {
            const checkLoginStatus = async () => {
                if (basicUserInfo && basicUserInfo.corpid === CHUNHUIZK_CORPID) {
                    setInternalViewPermission(true)
                }
            }

            checkLoginStatus()
        }

    }, [productStatus, basicUserInfo])

    if (productStatus === 'internal' && !internalViewPermission) {
        return (
            <div className="flex flex-col">
                <div className="text-center text-md my-2 p-2 bg-slate-700 text-white">
                    {t("products.loading")}
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col">
            <div className="text-right text-neutral-500 text-sm my-2">
                {t("products.updated_at")}: {format(new Date(product.utime), 'yyyy-MM-dd')}
            </div>

            {
                productStatus === 'internal' && (
                    <div className="text-center text-md mb-2 p-2 bg-orange-700 text-white rounded-sm">
                        {t(`products.${productStatus}`)}
                    </div>
                )
            }

            <div className="flex flex-col lg:flex-row">
                <div className="basis-1/3 overflow-scroll text-center mb-6 mr-0 lg:mr-8">
                    <ImageSlides
                        slides={pictures.map((p, index) => {
                            return {
                                url: p.large_thumbnail_full_path,
                                name: `${productName}_${index}`
                            }
                        })} />
                </div>
                <div className="basis-2/3 text-left">
                    <h2 className="text-xl">
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: "bold", color: "rgb(31, 112, 0)" }}>#{product.id} </span>
                        <LocalizedLink to={`/products/${product.categoryId}`} style={{ textDecoration: "underline" }}>{productCategoryName}</LocalizedLink>
                    </h2>
                    <h1 className="text-4xl my-1">{productName}</h1>
                    <p><span className="text-xl">{product.model}</span></p>
                    <h3 className="text-xl mt-4">{t('tags')}</h3>
                    <div className="mb-4 flex flex-wrap">
                        {
                            productTags.map((tagName, index) => (
                                <div className="px-2 m-1 rounded-md text-white bg-green-900" key={tagName}>
                                    <LocalizedLink to={tagIds ? `/products/tag/${tagIds[index]}` : '/'}>
                                        <span className="text-white text-sm">
                                            {tagName}
                                        </span>
                                    </LocalizedLink>
                                </div>
                            ))
                        }
                    </div>
                    {attachments.length > 0 ? (
                        <h3 className="text-xl">{t("相关资料下载")}</h3>
                    ) : null}
                    <div className="mb-4">
                        {attachments.map((o) => {
                            return (<div key={o.file_id}>
                                <a href={`https://general-service.chunhuizk.com/public/products/${product.id}/file/${o.file_id}`} download={o.original_file_name}>- {o.original_file_name}</a>
                            </div>)

                        })}
                    </div>
                    <Dialog
                        open={feedbackDialogOpen}
                        onOpenChange={(open) => setFeedbackDialogOpen(open)}>
                        <DialogTrigger onClick={() => setFeedbackDialogOpen(true)}>
                            <ShieldAlertIcon size={16} className="mr-1 text-red-600" /> {t('product-feedback-form.title')}
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{t('product-feedback-form.title')}</DialogTitle>
                                <DialogDescription>
                                    {t('product-feedback-form.dialog.description')}
                                </DialogDescription>
                            </DialogHeader>

                            <div>
                                <ProductFeedbackForm productCode={product.id} onClose={() => setFeedbackDialogOpen(false)} />
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>



            <div dangerouslySetInnerHTML={{ __html: productInfo }} className="text-left mt-6 pt-6 border-t product" />


        </div >
    )
}

export default ProductView